import React from 'react';

import { Box, Center, Flex, useTheme, Divider } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { Graph } from 'assets/assetsV1/iconComponents/Graph';
import { UpGraphIcon } from 'assets/assetsV1/iconComponents/UpGraphIcon';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextHeader1, TextLabel } from 'uiToolkitV1/Typography';

const TruncatedTextLabel = styled(TextLabel)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonInfo = ({ colors, data }) => (
  <Flex alignItems="center" p="4px" gap="12px">
    <Avatar src={data.imageUrl} />
    <Flex direction="column">
      <TextBodyMedium bold>{data.name}</TextBodyMedium>
      <TruncatedTextLabel sx={{ mb: 2 }} color={colors.secondary['666666']} maxWidth="190px">
        {data.email}
      </TruncatedTextLabel>
    </Flex>
  </Flex>
);

const PersonAndGridHover = ({ personHover, colors, data, buttonText, onClick, showViewDetailsBtn }) => (
  <>
    {personHover ? (
      <PersonInfo colors={colors} data={data} />
    ) : (
      <Flex p="4px 6px" gap="8px" direction="column">
        <Flex justifyContent="space-between">
          <Flex direction="column">
            <TextBodyMedium bold>Annual 2022</TextBodyMedium>
            <TextLabel>{data.members} Member</TextLabel>
          </Flex>
          <TextBodyMedium bold>{data.annualRating}</TextBodyMedium>
        </Flex>
      </Flex>
    )}
    <Divider sx={{ color: colors.feedback.F2F2F2 }} />
    <Flex p="4px 6px" gap="8px" direction="column">
      {personHover && data.annualRating ? (
        <Flex justifyContent="space-between">
          <TextBodyMedium bold>Annual 2022</TextBodyMedium>
          <TextBodyMedium bold>{data.annualRating}</TextBodyMedium>
        </Flex>
      ) : null}
      {data.ratings.map(({ name, value }) => {
        if (value === undefined || value === null) return null;
        return (
          <>
            {name === 'Overall' && <Divider sx={{ color: colors.feedback.F2F2F2 }} />}
            <Flex justifyContent="space-between">
              <TextBodyMedium>{name}</TextBodyMedium>
              <TextBodyMedium>{value}</TextBodyMedium>
            </Flex>
          </>
        );
      })}
      {/* <Flex justifyContent="space-between">
          <TextBodyMedium>Potential</TextBodyMedium>
          <TextBodyMedium>{data.potentialRating}</TextBodyMedium>
        </Flex>
        <Flex justifyContent="space-between">
          <TextBodyMedium>Performance</TextBodyMedium>
          <TextBodyMedium>{data.performanceRating}</TextBodyMedium>
        </Flex> */}
    </Flex>
    {showViewDetailsBtn ? (
      <>
        <Divider sx={{ color: colors.feedback.F2F2F2 }} />
        <Flex p="0 4px" gap="10px" justifyContent="center">
          <Button StartIcon={Graph} variant="tertiary" onClick={onClick}>
            {buttonText}
          </Button>
        </Flex>
      </>
    ) : null}
  </>
);

export const CalibrationGridHoverTooltip = ({
  personHover,
  data,
  onClick,
  isComparison,
  buttonText,
  showViewDetailsBtn = true,
}) => {
  const { colors } = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="column"
      bg={colors.common.white}
      p="8px"
      gap="4px"
      boxShadow="0px 2px 8px rgba(0, 0, 0, 0.08)"
      border={`1px solid ${colors.feedback.F2F2F2}`}
      borderRadius="12px"
      maxWidth={isComparison ? '350px' : '250px'}
    >
      {/* <PersonAndGridHover personHover={personHover} colors={colors} /> */}
      {isComparison ? (
        <>
          {personHover ? (
            <PersonInfo colors={colors} data={data} />
          ) : (
            <TextBodyMedium p="4px" bold>
              {data.gridRatingType}
            </TextBodyMedium>
          )}
          <Divider sx={{ color: colors.feedback.F2F2F2 }} />
          <Flex p="4px 0" gap="12px" justifyContent="space-between">
            <Flex p="8px 16px 12px 12px" direction="column" flex=".5">
              <Flex alignItems="baseline">
                <TextHeader1 bold pr={1}>
                  {data.currentRating}
                </TextHeader1>
                <UpGraphIcon color={colors.secondary['666666']} />
                <TextLabel bold pl={1} color={colors.secondary['666666']}>
                  {data.performancePercentage}%
                </TextLabel>
              </Flex>
              <TextLabel bold pb={2} color={colors.secondary['666666']}>
                {data.currentYear}
              </TextLabel>
              <Flex justifyContent="space-between" gap="4px" pb={1}>
                <TextLabel color={colors.secondary['666666']}>{personHover ? 'Potential' : 'Members'}</TextLabel>
                <TextLabel color={colors.secondary['666666']}>
                  {personHover ? data.potentialRating : data.members}
                </TextLabel>
              </Flex>
              <Flex justifyContent="space-between" gap="4px">
                <TextLabel color={colors.secondary['666666']}>{personHover ? 'Performance' : 'Avg. Score'}</TextLabel>
                <TextLabel color={colors.secondary['666666']}>
                  {personHover ? data.performanceRating : data.avgScore}
                </TextLabel>
              </Flex>
            </Flex>
            <Center height="50px" alignSelf="center">
              <Divider orientation="vertical" />
            </Center>
            <Flex p="8px 16px 12px 12px" direction="column" flex=".5">
              <TextHeader1 bold>{data.previousRating}</TextHeader1>
              <TextLabel bold pb={2} color={colors.secondary['666666']}>
                {data.previousYear}
              </TextLabel>
              <Flex justifyContent="space-between" gap="4px" pb={1}>
                <TextLabel color={colors.secondary['666666']}>{personHover ? 'Potential' : 'Members'}</TextLabel>
                <TextLabel color={colors.secondary['666666']}>
                  {personHover ? data.potentialRating : data.members}
                </TextLabel>
              </Flex>
              <Flex justifyContent="space-between" gap="4px">
                <TextLabel color={colors.secondary['666666']}>{personHover ? 'Performance' : 'Avg. Score'}</TextLabel>
                <TextLabel color={colors.secondary['666666']}>
                  {personHover ? data.performanceRating : data.avgScore}
                </TextLabel>
              </Flex>
            </Flex>
          </Flex>
          {showViewDetailsBtn ? (
            <>
              <Divider />
              <Flex p="0 4px" gap="10px" justifyContent="center">
                <Button StartIcon={Graph} variant="tertiary" onClick={onClick}>
                  {buttonText}
                </Button>
              </Flex>
            </>
          ) : null}
        </>
      ) : (
        <PersonAndGridHover
          colors={colors}
          personHover={personHover}
          data={data}
          onClick={onClick}
          isComparison={isComparison}
          buttonText={buttonText}
          showViewDetailsBtn={showViewDetailsBtn}
        />
      )}
    </Box>
  );
};
